// Maps NetSuite transaction statuses onto the design system's 4-step order
// tracker and status-badge colours (reference: Dens N Dente Tailwind.dc.html
// :5561-5581 — badge = success at the end, ocean in transit, brand before;
// tracker cur = terminal?4 : in-transit?2 : stage).

export const TRACKER_STEPS = [
  "Placed",
  "Processing",
  "On its way",
  "Delivered",
];

// Every web order ships with Purolator, so the detail page shows the carrier as a
// fixed label rather than `summary.shipcarrier`, which NetSuite returns as its
// generic ship-method text ("FedEx/USPS/More"). Swap for live data when carrier
// tracking lands.
export const CARRIER_NAME = "Purolator";

// Purolator's public tracking page for one PIN. It's the consumer-facing
// tracker (no login), so it's safe to link a customer straight to it — our own
// `usePurolatorTracking` call is authenticated and only yields the one-line
// status shown in the pill, whereas this page has the full scan history.
// Multi-package shipments come back from NetSuite as a comma-separated list,
// so callers must link each PIN separately rather than the raw field.
export const purolatorTrackingUrl = (pin) =>
  `https://www.purolator.com/en/shipping/tracker?pin=${encodeURIComponent(
    String(pin).trim()
  )}`;

// `BUILTIN.DF(trackingnumberlist)` renders several packages as comma-separated
// text — split so each PIN gets its own link.
export const splitTrackingNumbers = (trackingnumber) =>
  String(trackingnumber || "")
    .split(",")
    .map((pin) => pin.trim())
    .filter(Boolean);

// NetSuite's transaction status is a billing/approval workflow, not a
// fulfillment signal — "Billed" only means invoiced, and can happen before a
// package ever ships (e.g. paid-up-front orders). It used to be mapped to
// stage 4 ("Delivered") alongside real fulfillment statuses, which showed
// orders as delivered when they may not have shipped at all (fixed Sep 2026).
// So NetSuite's status alone only ever distinguishes two things: "Pending
// Fulfillment" (the order's very first status → "Order placed") vs anything
// else non-terminal (→ "Processing", regardless of which later NetSuite
// status it is — Approved, Billed, Fulfilled, whatever). "Shipped" comes from
// the SO's tracking number instead (NetSuite writes it only once a package is
// handed to Purolator), which both the list and the detail page have. Anything
// past that (Out for delivery / Delivered) can only come from live Purolator data,
// via `purolatorRailStage` below — and only the order-detail page fetches that.
const ENDED = new Set(["Cancelled", "Closed", "Refunded"]);

// The order-detail page's 5-step rail (reference ORDER DETAIL / TRACKING,
// Dens N Dente Tailwind.dc.html:2057-2076). Rail cur maps from the 4-step list
// stage: 0→0, 1→1, 2→2, terminal(4)→5 = all five done. Index 3 is the reference's
// own "Out for delivery" wording (it was briefly "In transit"). It's one stage,
// not two: every non-terminal Purolator status past "shipment created" — in
// transit, picked up, at a facility, and Purolator's literal "Out for Delivery"
// — lands here, so the label is the last thing that happens in it rather than
// a claim about where the package is right now. See `purolatorRailStage`.
export const DETAIL_TRACKER_STEPS = [
  "Order placed",
  "Processing",
  "Shipped",
  "Out for delivery",
  "Delivered",
];

// Hero-band icon per rail step (filled Material Symbols); `cancel` when
// cancelled. Index with Math.min(cur, 4).
export const DETAIL_STEP_ICONS = [
  "receipt_long",
  "inventory_2",
  "local_shipping",
  "local_shipping",
  "task_alt",
];

// Live Purolator status (from usePurolatorTracking) is the only thing allowed
// to advance the rail past "Shipped" (index 2) — see the note above `ENDED`.
// Returned value is on the rail's own 0-5 scale (5 dots, 5 = all done), not the
// 4-step list scale `getOrderTracking` uses.
//
// The rail has no "in transit" step, so each stage means exactly what it says:
// "Out for delivery" is shown **only** when Purolator itself reports out for
// delivery (status code `OFD`). Every other non-terminal scan — picked up, in
// transit, arrived at a facility, shipment created — stays at "Shipped", which
// is true of all of them, rather than overstating where the package is.
//
// Matching is on the code first (`OFD` is stable; the description's wording
// isn't), with the description as a fallback. Note "Out for Delivery" contains
// /delivery/ (noun) and not /delivered/ (verb) — the terminal check relies on
// that distinction, so keep it first and keep it matching the verb.
function purolatorRailStage(purolatorStatus) {
  const code = purolatorStatus?.code || "";
  const description = purolatorStatus?.description || "";
  if (!code && !description) return null;
  if (/delivered/i.test(description) || /^DEL$/i.test(code)) {
    return 5; // terminal: all five dots done
  }
  if (/^OFD$/i.test(code) || /out\s*for\s*delivery/i.test(description)) {
    return 3; // "Out for delivery" current
  }
  return 2; // created / picked up / in transit / at facility: "Shipped" current
}

export function getOrderDetailTracking(
  rawStatus,
  trackingnumber,
  fulfillmentStatus,
  purolatorStatus
) {
  const base = getOrderTracking(rawStatus, trackingnumber);
  if (base.cancelled) return { ...base, cur: 0 };

  let cur = base.cur; // 0 or 1, from the tracking number + NetSuite status

  // The linked Item Fulfillment record. Its mere existence means the warehouse
  // has started work on the order, so it can't still be sitting at "Order
  // placed" — that's true even while the SO's own status is "Pending
  // Fulfillment". `Picked` and `Packed` are real statuses on this account, not
  // hypothetical.
  if (fulfillmentStatus) cur = Math.max(cur, 1);
  // Only its "Shipped" status means a package is actually gone. Since
  // `getOrderTracking` already promotes on the tracking number alone, this
  // matters for the fulfilled order whose tracking number hasn't been written
  // back yet.
  if (fulfillmentStatus === "Shipped") cur = Math.max(cur, 2);

  // Live Purolator status, once available, takes precedence over both of
  // the above — it's the most accurate signal we have.
  const liveRailStage = purolatorRailStage(purolatorStatus);
  if (liveRailStage != null) cur = Math.max(cur, liveRailStage);

  return {
    ...base,
    cur,
    badgeColor: cur >= 5 ? "success" : cur >= 2 ? "ocean" : "brand",
  };
}

export function getOrderTracking(rawStatus, trackingnumber) {
  const status = rawStatus || "Pending Fulfillment";
  const cancelled = ENDED.has(status);
  // The tracking number is the one NetSuite-side field that does mean the order
  // physically shipped — it's written onto the linked Item Fulfillment (not the
  // SO, whose own `trackingnumberlist` is empty on this account) once a package
  // is packed and handed to Purolator. So its presence promotes the order to
  // "On its way" (stage 2) regardless of the transaction status.
  const shipped = !cancelled && Boolean(trackingnumber);
  // Otherwise: "Pending Fulfillment" is the order's very first NetSuite status
  // → "Order placed". Every other non-cancelled status collapses to
  // "Processing" — see the note above `ENDED` for why fulfillment-sounding
  // statuses aren't trusted to mean the order actually shipped.
  const cur = cancelled
    ? 0
    : shipped
      ? 2
      : status === "Pending Fulfillment"
        ? 0
        : 1;
  return {
    cancelled,
    cur,
    stepNow: TRACKER_STEPS[Math.min(cur, 3)],
    stepPos: Math.min(cur + 1, 4),
    badgeColor: cancelled ? "neutral" : shipped ? "ocean" : "brand",
    // Always the real NetSuite status — the Orders & tracking list's Status
    // column should show what NetSuite actually says (Billed, Partially
    // Fulfilled, Pending Fulfillment, ...), not a derived "Shipped" label
    // that papers over it. `shipped` still drives the tracker/badge color;
    // it just no longer overrides this text.
    label: status,
  };
}
